import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Trophy, Sparkles, TrendingUp, Users, ArrowUpRight, RefreshCw, Star,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMonth = (yyyymm) => {
  if (!yyyymm || !yyyymm.includes('-')) return '';
  const [y, m] = yyyymm.split('-').map((s) => Number(s));
  try {
    return new Date(y, m - 1, 1).toLocaleString('en-CA', { month: 'long', year: 'numeric' });
  } catch { return yyyymm; }
};

// Render a 5-star rating bar from a 0–100% performance score.
const StarBar = ({ score }) => {
  const stars = Math.max(0, Math.min(5, Math.round((Number(score) || 0) / 20)));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${score}% rating`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < stars ? 'text-[#FFD166] fill-[#FFD166]' : 'text-[#E8E5DF]'}`}
          strokeWidth={2}
        />
      ))}
      <span className="ml-1.5 text-xs font-medium text-[#1E1E24]">{score}%</span>
    </div>
  );
};

export const CrmStaffPerformance = () => {
  const todayMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [month, setMonth] = useState(todayMonth);
  const [data, setData] = useState({ month: todayMonth, rows: [], thresholds: { conversions: 10, bookings: 5 } });
  const [loading, setLoading] = useState(true);

  const fetchData = async (m = month) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/employees/performance?month=${m}`, { withCredentials: true });
      setData(res.data || { month: m, rows: [], thresholds: { conversions: 10, bookings: 5 } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load performance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(month); /* eslint-disable-next-line */ }, [month]);

  const rows = data.rows || [];
  const eomRow = rows.find((r) => r.employee_of_the_month);

  // Aggregate metrics for the summary tiles
  const totals = useMemo(() => {
    const conv = rows.reduce((s, r) => s + (r.monthly_conversions || 0), 0);
    const bookings = rows.reduce((s, r) => s + (r.monthly_bookings || 0), 0);
    const avgRating = rows.length
      ? Math.round((rows.reduce((s, r) => s + (r.performance_rating || 0), 0) / rows.length) * 10) / 10
      : 0;
    return { conv, bookings, avgRating };
  }, [rows]);

  return (
    <div data-testid="crm-staff-performance">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-6 h-6 text-[#FFD166]" />
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Employee Performance
            </h1>
          </div>
          <p className="text-[#6B6B7A] text-sm">
            Prospect-to-customer conversions, repeat bookings, and the Employee of the Month for {fmtMonth(data.month)}.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value || todayMonth)}
            className="w-44 rounded-xl border-[#E8E5DF]"
            data-testid="staff-perf-month"
          />
          <Button variant="outline" onClick={() => fetchData(month)} className="rounded-full border-[#E8E5DF]" data-testid="staff-perf-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Employee of the Month banner */}
      {eomRow ? (
        <div
          className="relative overflow-hidden rounded-2xl border-2 border-[#FFD166] bg-gradient-to-r from-[#FFF8E7] via-[#FFFDF8] to-[#FFE9A8] p-5 mb-6 flex items-start gap-4"
          data-testid="staff-perf-eom-banner"
        >
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#FFD166]/30 blur-2xl" aria-hidden />
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FFD166] flex items-center justify-center shadow-md">
            <Trophy className="w-6 h-6 text-[#9C6B12]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-[#9C6B12] font-semibold">⭐ Employee of the Month · {fmtMonth(data.month)}</p>
            <p className="text-xl font-semibold text-[#1E1E24] mt-0.5" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {eomRow.name}
            </p>
            <p className="text-sm text-[#6B6B7A]">
              {eomRow.monthly_conversions} conversions · {eomRow.monthly_bookings} repeat bookings · {eomRow.performance_rating}% rating
            </p>
          </div>
          <Sparkles className="w-6 h-6 text-[#FFD166] mt-1 animate-pulse" aria-hidden />
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 mb-6 text-sm text-[#6B6B7A]" data-testid="staff-perf-eom-empty">
          <strong className="text-[#1E1E24]">No Employee of the Month yet.</strong>{' '}
          The badge is awarded when one teammate hits <strong>{data.thresholds?.conversions ?? 10}+ conversions</strong>{' '}
          and <strong>{data.thresholds?.bookings ?? 5}+ repeat bookings</strong> in the same month.
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryTile icon={Users} label="Conversions this month" value={totals.conv} accent="#FF3B6B" testid="staff-perf-total-conversions" />
        <SummaryTile icon={ArrowUpRight} label="Repeat bookings" value={totals.bookings} accent="#06D6A0" testid="staff-perf-total-bookings" />
        <SummaryTile icon={TrendingUp} label="Avg team rating" value={`${totals.avgRating}%`} accent="#9C6B12" testid="staff-perf-avg-rating" />
      </div>

      {/* Performance table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead className="w-32">Conversions</TableHead>
              <TableHead className="w-32">Bookings</TableHead>
              <TableHead className="w-32">Success rate</TableHead>
              <TableHead className="w-44">Performance rating</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]" data-testid="staff-perf-empty">
                  No employees yet.
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.user_id} className="hover:bg-[#FFFDF8]" data-testid={`staff-perf-row-${r.user_id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#1E1E24]">{r.name}</span>
                    {r.employee_of_the_month && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFD166]/30 text-[#9C6B12] font-semibold">
                        <Trophy className="w-3 h-3" /> EOM
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B6B7A]">{r.email} · {r.role}</p>
                </TableCell>
                <TableCell>
                  <span className="text-sm font-medium text-[#1E1E24]" data-testid={`staff-perf-conv-${r.user_id}`}>
                    {r.monthly_conversions}
                  </span>
                  <span className="text-xs text-[#6B6B7A] ml-1">/ mo</span>
                  <p className="text-[11px] text-[#6B6B7A]">all-time: {r.total_conversions}</p>
                </TableCell>
                <TableCell>
                  <span className="text-sm font-medium text-[#1E1E24]" data-testid={`staff-perf-bookings-${r.user_id}`}>
                    {r.monthly_bookings}
                  </span>
                  <p className="text-[11px] text-[#6B6B7A]">all-time: {r.total_bookings}</p>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-[#1E1E24]" data-testid={`staff-perf-success-${r.user_id}`}>{r.success_rate}%</span>
                </TableCell>
                <TableCell>
                  <StarBar score={r.performance_rating} />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/crm/employees/${r.user_id}`}
                    className="text-xs text-[#FF3B6B] hover:underline"
                    data-testid={`staff-perf-view-${r.user_id}`}
                  >
                    View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-[#6B6B7A] mt-4">
        Performance rating starts at a 50% team baseline and climbs to 100% as the employee hits the {data.thresholds?.conversions ?? 10}-conversion monthly target. Updated live as prospects are converted.
      </p>
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex items-center gap-3" data-testid={testid}>
    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}1A` }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs text-[#6B6B7A]">{label}</p>
      <p className="text-xl font-semibold text-[#1E1E24]">{value}</p>
    </div>
  </div>
);

export default CrmStaffPerformance;
